import * as React from "react";
import {createRoot, type Root} from "react-dom/client";

import {RenderStateRoot} from "@khanacademy/wonder-blocks-core";
import {GqlRouter} from "@repo/data-gql";
import {ApolloRouter} from "@repo/data-gql-apollo";
import {LinguiI18nProvider} from "@repo/i18n";

const roots = new Map<HTMLElement, Root>();

/**
 * Get a React root for the given container.
 *
 * If a root already exists for this container, use it, otherwise create and
 * store a new root.
 *
 */
const getOrCreateRoot = (container: HTMLElement): Root => {
    const existingRoot = roots.get(container);
    if (existingRoot) {
        return existingRoot;
    }

    // This is a deprecated way of doing things, we should be using
    // React Portals.
    // eslint-disable-next-line no-restricted-syntax
    const newRoot = createRoot(container);
    roots.set(container, newRoot);
    return newRoot;
};

/**
 * Unmount the root for the given container.
 */
const unmountRoot = (container: HTMLElement) => {
    const root = roots.get(container);
    if (root) {
        // We cannot unmount immediately because this might be called from
        // within a React render, and React does not allow unmounting during
        // render. So we defer the unmount to the next tick.
        // eslint-disable-next-line no-restricted-syntax
        setTimeout(() => root.unmount());
        roots.delete(container);
    }
};

/**
 * @deprecated Please use React Portals instead.
 *
 * @returns A function that unmounts the React tree.
 */
export default function deprecatedReactRender(
    element: React.ReactNode,
    container: HTMLElement,
    callback?: () => void,
): () => void {
    const ConfirmMount = ({children}: {children: React.ReactNode}) => {
        React.useEffect(() => {
            callback?.();
        }, []);
        return <>{children}</>;
    };

    const root = getOrCreateRoot(container);
    root.render(
        /**
         * `RenderStateRoot` is responsible for tracking whether it's the
         * initial render or subsequent renders.  It's used by a number of
         * components.  We're placing it as high up in the render tree as
         * possible to ensure that any components using that hook or those
         * components will function correctly.
         */
        <ConfirmMount>
            <RenderStateRoot throwIfNested={false}>
                <LinguiI18nProvider>
                    <GqlRouter>
                        <ApolloRouter>{element}</ApolloRouter>
                    </GqlRouter>
                </LinguiI18nProvider>
            </RenderStateRoot>
        </ConfirmMount>,
    );

    return () => unmountRoot(container);
}
